import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';

Modal.setAppElement('#root');

export default function AuthModal({ isOpen, onClose }) {
  const { continueAsGuest, login, register } = useAuth();
  const [tab, setTab] = useState('login'); // 'login' | 'register'
  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
  });
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Autofocus first field when opening
  const firstRef = useRef(null);
  useEffect(() => {
    if (isOpen) setTimeout(() => firstRef.current?.focus(), 0);
  }, [isOpen]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (formError) setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFormError('');

    const payload = {
      username: form.username.trim(),
      email: form.email.trim(),
      password: form.password,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
    };

    try {
      if (tab === 'login') {
        await login(payload.username, payload.password);
        toast.success('Logged in!');
      } else {
        await register(
          payload.username,
          payload.email || null,
          payload.password,
          payload.firstName,
          payload.lastName
        );
        toast.success('Account created!');
      }
      setForm({ username: '', email: '', password: '', firstName: '', lastName: '' });
      onClose?.();
    } catch (err) {
      const msg = err?.message || 'Authentication failed';
      setFormError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGuest = async () => {
    setLoading(true);
    setFormError('');
    try {
      await continueAsGuest();
      toast.success('Continuing as guest');
      onClose?.();
    } catch (err) {
      const msg = err?.message || 'Guest error';
      setFormError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => !loading && onClose?.()}
      className="relative bg-[#0f0f0f] text-white p-6 rounded-lg shadow-xl
                 max-w-md w-[92%] max-h-[90vh] overflow-y-auto"
      overlayClassName="fixed inset-0 bg-black/60 backdrop-blur-[2px] z-[100]
                        flex justify-center items-start sm:items-center p-4"
    >
      <button
        onClick={() => !loading && onClose?.()}
        className="absolute top-2 right-2 text-gray-400 hover:text-white
                   h-10 w-10 flex items-center justify-center rounded"
        aria-label="Close authentication modal"
      >
        ✕
      </button>

      <h2 className="text-xl font-bold mb-4 pr-10">Authentication</h2>

      <div className="flex gap-2 mb-3">
        <button
          onClick={() => { setTab('login'); setFormError(''); }}
          className={`px-3 py-2 rounded text-sm h-11 ${tab === 'login' ? 'bg-blue-600' : 'bg-gray-700'}`}
          disabled={loading}
        >
          Login
        </button>
        <button
          onClick={() => { setTab('register'); setFormError(''); }}
          className={`px-3 py-2 rounded text-sm h-11 ${tab === 'register' ? 'bg-blue-600' : 'bg-gray-700'}`}
          disabled={loading}
        >
          Register
        </button>
      </div>

      {formError && (
        <div className="mb-3 text-sm text-red-400 bg-red-900/20 border border-red-800 rounded p-2">
          {formError}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-3">
        {tab === 'register' && (
          <>
            <input
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              placeholder="First Name"
              className="w-full h-11 px-3 bg-gray-900 border border-gray-600 rounded
                         focus:border-blue-500 focus:outline-none"
              required
              disabled={loading}
            />
            <input
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              placeholder="Last Name"
              className="w-full h-11 px-3 bg-gray-900 border border-gray-600 rounded
                         focus:border-blue-500 focus:outline-none"
              required
              disabled={loading}
            />
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Email (optional)"
              className="w-full h-11 px-3 bg-gray-900 border border-gray-600 rounded
                         focus:border-blue-500 focus:outline-none"
              disabled={loading}
            />
          </>
        )}
        <input
          ref={firstRef}
          name="username"
          value={form.username}
          onChange={handleChange}
          placeholder="Username"
          className="w-full h-11 px-3 bg-gray-900 border border-gray-600 rounded
                     focus:border-blue-500 focus:outline-none"
          required
          disabled={loading}
        />
        <input
          type="password"
          name="password"
          value={form.password}
          onChange={handleChange}
          placeholder="Password"
          className="w-full h-11 px-3 bg-gray-900 border border-gray-600 rounded
                     focus:border-blue-500 focus:outline-none"
          required
          disabled={loading}
        />
        <button
          type="submit"
          className="w-full h-11 bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-60"
          disabled={loading}
        >
          {loading ? 'Please wait…' : tab === 'login' ? 'Login' : 'Register'}
        </button>
      </form>

      <div className="mt-4 text-center">
        <button
          onClick={handleGuest}
          className="text-sm text-blue-400 hover:text-blue-300 disabled:opacity-60"
          disabled={loading}
        >
          Continue as Guest
        </button>
      </div>
    </Modal>
  );
}
